<template>
  <div>
    <div class="space-y-6">
      <!-- 统计卡片 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        <div
          v-for="stat in stats"
          :key="stat.key"
          class="bg-white overflow-hidden shadow-sm rounded-lg border border-gray-200"
        >
          <div class="p-5">
            <div class="flex items-center justify-between">
              <div class="flex-1">
                <!-- 标题和sublabel在同一行 -->
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-sm font-medium text-gray-600">{{ stat.label }}</span>
                  <span v-if="stat.sublabel" class="text-xs text-gray-500">{{ stat.sublabel }}</span>
                </div>
                <div>
                  <span v-if="stat.loading" class="text-2xl font-bold text-gray-900">-</span>
                  <span v-else-if="stat.isSubAccountMessage" class="text-sm text-gray-500 font-normal">{{ stat.value }}</span>
                  <span v-else class="text-2xl font-bold text-gray-900">{{ stat.value }}</span>
                </div>
              </div>
              <div class="flex-shrink-0">
                <div :class="['p-3 rounded-lg', stat.bgColor]">
                  <component
                    :is="stat.icon"
                    :class="['h-6 w-6', stat.iconColor]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 快速操作 -->
      <div>
        <h2 class="text-lg font-medium text-gray-900 mb-4">快速操作</h2>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <button
            v-for="action in quickActions"
            :key="action.key"
            @click="action.action"
            class="group bg-white p-4 rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 text-center"
          >
            <div class="flex flex-col items-center space-y-2">
              <div :class="['p-2.5 rounded-lg group-hover:scale-105 transition-transform', action.bgColor]">
                <component
                  :is="action.icon"
                  :class="['h-5 w-5', action.iconColor]"
                />
              </div>
              <div>
                <h3 class="text-sm font-medium text-gray-900 group-hover:text-gray-700">
                  {{ action.label }}
                </h3>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- 数据统计图表 -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- 邮件发送趋势 -->
        <div class="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-base font-semibold text-gray-900">邮件发送趋势</h3>
              <p v-if="emailTrendSummary" class="text-xs text-gray-500 mt-1">
                总发送 {{ emailTrendSummary.total_sent }} 封 · 打开率 {{ emailTrendSummary.open_rate }}%
              </p>
            </div>
            <div class="relative" ref="emailTrendSelectRef" @click.stop>
              <!-- 触发器 -->
              <div
                @click.stop="toggleEmailTrendDropdown"
                class="relative w-32 h-9 cursor-pointer bg-white border border-gray-300 rounded-lg px-3 text-left shadow-sm hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-colors flex items-center"
                :class="{ 'border-blue-500 ring-1 ring-blue-500': isEmailTrendOpen }"
              >
                <span class="flex-1 truncate text-sm text-gray-900">
                  {{ emailTrendOptions.find(o => o.value === emailTrendDays)?.label || '选择时间' }}
                </span>
                <span class="pointer-events-none flex items-center pl-2">
                  <svg
                    class="h-5 w-5 text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': isEmailTrendOpen }"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                  </svg>
                </span>
              </div>

              <!-- 下拉列表 -->
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-show="isEmailTrendOpen"
                  class="absolute z-50 mt-1 w-32 bg-white shadow-lg max-h-48 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                  style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;"
                >
                  <div
                    v-for="option in emailTrendOptions"
                    :key="option.value"
                    @click.stop="selectEmailTrendDays(option.value)"
                    class="group relative cursor-pointer select-none py-3 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                    :class="{ 'bg-blue-100 text-blue-900': emailTrendDays === option.value }"
                  >
                    <span class="block truncate text-sm font-medium">
                      {{ option.label }}
                    </span>
                    <span
                      v-if="emailTrendDays === option.value"
                      class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                    >
                      <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>
              </transition>
            </div>
          </div>
          <div class="h-80" v-if="emailTrendLoading">
            <div class="flex items-center justify-center h-full">
              <div class="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          </div>
          <div class="h-80" v-else-if="emailTrendData">
            <LineChart 
              chart-id="emailTrendChart" 
              :data="emailTrendData"
            />
          </div>
          <div class="h-80 flex items-center justify-center text-gray-400" v-else>
            <p class="text-sm">暂无数据</p>
          </div>
        </div>

        <!-- 客户增长趋势 -->
        <div class="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h3 class="text-base font-semibold text-gray-900">客户增长</h3>
              <div v-if="customerGrowthRate !== null" class="flex items-center space-x-2 text-sm mt-1">
                <span :class="[
                  'font-medium',
                  customerGrowthRate >= 0 ? 'text-green-600' : 'text-red-600'
                ]">
                  {{ customerGrowthRate >= 0 ? '↑' : '↓' }} {{ Math.abs(customerGrowthRate) }}%
                </span>
                <span class="text-gray-500">vs 上期</span>
              </div>
            </div>
            <div class="relative" ref="customerGrowthSelectRef" @click.stop>
              <!-- 触发器 -->
              <div
                @click.stop="toggleCustomerGrowthDropdown"
                class="relative w-36 h-9 cursor-pointer bg-white border border-gray-300 rounded-lg px-3 text-left shadow-sm hover:border-blue-500 hover:ring-1 hover:ring-blue-500 transition-colors flex items-center"
                :class="{ 'border-blue-500 ring-1 ring-blue-500': isCustomerGrowthOpen }"
              >
                <span class="flex-1 truncate text-sm text-gray-900">
                  {{ customerGrowthOptions.find(o => o.value === customerGrowthPeriod)?.label || '选择周期' }}
                </span>
                <span class="pointer-events-none flex items-center pl-2">
                  <svg
                    class="h-5 w-5 text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': isCustomerGrowthOpen }"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
                  </svg>
                </span>
              </div>

              <!-- 下拉列表 -->
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div
                  v-show="isCustomerGrowthOpen"
                  class="absolute z-50 mt-1 w-36 bg-white shadow-lg max-h-48 rounded-lg py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                  style="scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;"
                >
                  <div
                    v-for="option in customerGrowthOptions"
                    :key="option.value"
                    @click.stop="selectCustomerGrowthPeriod(option.value)"
                    class="group relative cursor-pointer select-none py-3 pl-3 pr-9 text-gray-900 hover:bg-blue-50 hover:text-blue-900 transition-colors"
                    :class="{ 'bg-blue-100 text-blue-900': customerGrowthPeriod === option.value }"
                  >
                    <span class="block truncate text-sm font-medium">
                      {{ option.label }}
                    </span>
                    <span
                      v-if="customerGrowthPeriod === option.value"
                      class="absolute inset-y-0 right-0 flex items-center pr-3 text-blue-600"
                    >
                      <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </div>
              </transition>
            </div>
          </div>
          <div class="h-80" v-if="customerGrowthLoading">
            <div class="flex items-center justify-center h-full">
              <div class="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          </div>
          <div class="h-80" v-else-if="customerGrowthData">
            <LineChart 
              chart-id="customerGrowthChart" 
              :data="customerGrowthData"
            />
          </div>
          <div class="h-80 flex items-center justify-center text-gray-400" v-else>
            <p class="text-sm">暂无数据</p>
          </div>
        </div>

        <!-- 营销活动效果 -->
        <div class="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-base font-semibold text-gray-900">营销活动效果</h3>
            <button 
              @click="goToCampaigns"
              class="text-sm text-blue-600 hover:text-blue-700"
            >
              查看全部
            </button>
          </div>
          <div class="h-80" v-if="campaignStatsLoading">
            <div class="flex items-center justify-center h-full">
              <div class="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          </div>
          <div class="h-80" v-else-if="campaignStatsData">
            <BarChart 
              chart-id="campaignStatsChart" 
              :data="campaignStatsData"
            />
          </div>
          <div class="h-80 flex items-center justify-center text-gray-400" v-else>
            <p class="text-sm">暂无数据</p>
          </div>
        </div>

        <!-- 最近活动列表 -->
        <div class="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-base font-semibold text-gray-900">最近活动</h3>
            <button 
              @click="goToCampaigns"
              class="text-sm text-blue-600 hover:text-blue-700"
            >
              查看全部
            </button>
          </div>
          <div v-if="recentActivitiesLoading" class="flex items-center justify-center h-80">
            <div class="animate-spin w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
          <div v-else-if="recentActivities && recentActivities.length > 0" class="space-y-3 h-80 overflow-y-auto">
            <div 
              v-for="(activity, index) in recentActivities" 
              :key="activity.id"
              @click="viewCampaign(activity.id)"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <div class="flex items-center space-x-3">
                <div :class="[
                  'p-2 rounded-lg',
                  index === 0 ? 'bg-blue-100' : index === 1 ? 'bg-green-100' : 'bg-purple-100'
                ]">
                  <EnvelopeIcon :class="[
                    'h-5 w-5',
                    index === 0 ? 'text-blue-600' : index === 1 ? 'text-green-600' : 'text-purple-600'
                  ]" />
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">{{ activity.name }}</p>
                  <p class="text-xs text-gray-500">
                    已发送 {{ activity.sent_count }} 封，打开率 {{ activity.open_rate }}%
                  </p>
                </div>
              </div>
              <span class="text-xs text-gray-400">{{ activity.time }}</span>
            </div>
            <div class="text-center pt-2">
              <button 
                @click="goToCampaigns"
                class="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                查看全部活动 →
              </button>
            </div>
          </div>
          <div v-else class="flex items-center justify-center h-80 text-gray-400">
            <p class="text-sm">暂无活动数据</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTabsStore } from '@/stores/tabs'
import { apiRequest } from '@/utils/api'
import { useToast } from 'vue-toastification'
import {
  EnvelopeIcon,
  UsersIcon,
  UserGroupIcon,
  QrCodeIcon,
  BuildingStorefrontIcon,
  PlusIcon,
  InboxIcon,
  BellIcon
} from '@heroicons/vue/24/outline'
import LineChart from '@/components/charts/LineChart.vue'
import BarChart from '@/components/charts/BarChart.vue'
import dashboardService from '@/services/dashboardService'

const router = useRouter()
const authStore = useAuthStore()
const tabsStore = useTabsStore()
const toast = useToast()

// 图表数据
const emailTrendData = ref(null)
const emailTrendSummary = ref(null)
const emailTrendLoading = ref(false)
const emailTrendDays = ref(7)

const campaignStatsData = ref(null)
const campaignStatsLoading = ref(false)

const customerGrowthData = ref(null)
const customerGrowthRate = ref(null)
const customerGrowthLoading = ref(false)
const customerGrowthPeriod = ref('day')

const recentActivities = ref([])
const recentActivitiesLoading = ref(false)

// 下拉框状态
const isEmailTrendOpen = ref(false)
const emailTrendSelectRef = ref(null)
const isCustomerGrowthOpen = ref(false)
const customerGrowthSelectRef = ref(null)

// 下拉框选项
const emailTrendOptions = [
  { value: 7, label: '最近7天' },
  { value: 30, label: '最近30天' },
  { value: 90, label: '最近90天' }
]

const customerGrowthOptions = [
  { value: 'day', label: '最近30天' },
  { value: 'week', label: '最近3个月' },
  { value: 'month', label: '最近12个月' }
]

// 统计数据
const stats = ref([
  {
    key: 'qr_codes',
    label: '售后二维码',
    sublabel: '今日访问量',
    value: '-',
    icon: QrCodeIcon,
    iconColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
    loading: true,
    showVisits: true  // 标记为显示访问量
  },
  {
    key: 'brand_sites',
    label: '品牌独立站',
    sublabel: '今日访问量',
    value: '-',
    icon: BuildingStorefrontIcon,
    iconColor: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    loading: true,
    showVisits: true  // 标记为显示访问量
  },
  {
    key: 'customers',
    label: '客户数',
    value: '-',
    unlimited: true, // 无限制状态
    icon: UsersIcon,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
    loading: true
  },
  {
    key: 'sub_accounts',
    label: '子账号',
    value: '-',
    progress: 0,
    icon: UserGroupIcon,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
    loading: true
  },
  {
    key: 'email_accounts',
    label: '邮件账户',
    value: '-',
    progress: 0,
    icon: EnvelopeIcon,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    loading: true
  }
])

// 快速操作
const quickActions = ref([
  {
    key: 'email_accounts',
    label: '邮件账户管理',
    description: '连接新的邮箱账户',
    icon: PlusIcon,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    action: () => tabsStore.addTab({
      path: '/email-accounts',
      name: 'EmailAccounts',
      params: {},
      query: {}
    })
  },
  {
    key: 'customers',
    label: '客户管理',
    description: '批量导入客户数据',
    icon: UsersIcon,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
    action: () => tabsStore.addTab({
      path: '/customers',
      name: 'Customers',
      params: {},
      query: {}
    })
  },
  {
    key: 'qr_codes',
    label: '售后二维码',
    description: '创建和管理二维码',
    icon: QrCodeIcon,
    iconColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
    action: () => tabsStore.addTab({
      path: '/qr-code',
      name: 'QrCode',
      params: {},
      query: {}
    })
  },
  {
    key: 'brand_sites',
    label: '品牌独立站',
    description: '即将推出',
    icon: BuildingStorefrontIcon,
    iconColor: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    action: () => tabsStore.addTab({
      path: '/brand-site',
      name: 'BrandSite',
      params: {},
      query: {}
    })
  },
  {
    key: 'emails',
    label: '邮件管理',
    description: '聚合邮件管理',
    icon: InboxIcon,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-50',
    action: () => tabsStore.addTab({
      path: '/emails',
      name: 'Emails',
      params: {},
      query: {}
    })
  },
  {
    key: 'campaigns',
    label: '营销活动',
    description: '创建营销活动',
    icon: BellIcon,
    iconColor: 'text-pink-600',
    bgColor: 'bg-pink-50',
    action: () => tabsStore.addTab({
      path: '/campaigns',
      name: 'Campaigns',
      params: {},
      query: {}
    })
  }
])

// 格式化配额显示
const formatQuotaValue = (current, max) => {
  // 如果最大值为0，显示 -
  if (max === 0) {
    return '-'
  }
  // 正常显示 current/max
  return `${current}/${max}`
}

// 加载仪表板数据
const loadDashboardData = async () => {
  // 检查用户是否已认证，如果未认证则不发起请求
  if (!authStore.isAuthenticated) {
    return
  }
  
  try {
    const response = await apiRequest.get('/api/accounts/stats/')
    
    // 检查响应数据结构
    let data;
    if (response.data.success) {
      data = response.data.data
    } else if (response.data.data) {
      // 有些API可能直接返回data字段
      data = response.data.data
    } else {
      // 直接使用响应数据
      data = response.data
    }
    
    
    // 更新客户数
    const customerStat = stats.value.find(s => s.key === 'customers')
    if (customerStat) {
      customerStat.value = data.contacts_count || 0
      customerStat.loading = false
    }
    
    // 更新子账号配额
    const subAccountStat = stats.value.find(s => s.key === 'sub_accounts')
    if (subAccountStat && data.sub_accounts_quota) {
      // 如果是子账户，显示特殊消息
      if (data.sub_accounts_quota.is_sub_account) {
        subAccountStat.value = data.sub_accounts_quota.message || '-'
        subAccountStat.progress = undefined
        subAccountStat.unlimited = undefined
        subAccountStat.isSubAccountMessage = true
      } else {
        const current = data.sub_accounts_quota.current || 0
        const max = data.sub_accounts_quota.max || 0
        subAccountStat.value = formatQuotaValue(current, max)
        subAccountStat.progress = max > 0 ? Math.round((current / max) * 100) : 0
        subAccountStat.isSubAccountMessage = false
      }
      subAccountStat.loading = false
    }
    
    // 更新邮件账户配额
    const emailAccountStat = stats.value.find(s => s.key === 'email_accounts')
    if (emailAccountStat && data.email_accounts_quota) {
      // 如果是子账户，显示特殊消息
      if (data.email_accounts_quota.is_sub_account) {
        emailAccountStat.value = data.email_accounts_quota.message || '-'
        emailAccountStat.progress = undefined
        emailAccountStat.unlimited = undefined
        emailAccountStat.isSubAccountMessage = true
      } else {
        const current = data.email_accounts_quota.current || 0
        const max = data.email_accounts_quota.max || 0
        emailAccountStat.value = formatQuotaValue(current, max)
        emailAccountStat.progress = max > 0 ? Math.round((current / max) * 100) : 0
        emailAccountStat.isSubAccountMessage = false
      }
      emailAccountStat.loading = false
    }
    
    // 更新售后二维码今日访问量
    const qrCodeStat = stats.value.find(s => s.key === 'qr_codes')
    if (qrCodeStat) {
      // 显示今日访问量（从后端获取，暂时显示0）
      qrCodeStat.value = data.qr_codes_today_visits || 0
      qrCodeStat.loading = false
    }
    
    // 更新品牌独立站今日访问量
    const brandSiteStat = stats.value.find(s => s.key === 'brand_sites')
    if (brandSiteStat) {
      // 显示今日访问量（从后端获取，暂时显示0）
      brandSiteStat.value = data.brand_sites_today_visits || 0
      brandSiteStat.loading = false
    }
  } catch (error) {
    // 如果是主动退出登录或用户未认证，不显示错误提示
    if (!window.isLoggingOut && authStore.isAuthenticated) {
      console.error('加载仪表板数据失败:', error)
      toast.error('加载仪表板数据失败')
    }
    
    // 设置默认值
    stats.value.forEach(stat => {
      stat.loading = false
      if (stat.value === '-') {
        stat.value = '0'
      }
    })
  }
}

// 加载邮件趋势数据
const loadEmailTrend = async () => {
  emailTrendLoading.value = true
  try {
    const result = await dashboardService.getEmailTrend(emailTrendDays.value)
    if (result.success && result.data) {
      emailTrendData.value = result.data
      emailTrendSummary.value = result.data.summary
    }
  } catch (error) {
    console.error('加载邮件趋势失败:', error)
  } finally {
    emailTrendLoading.value = false
  }
}

// 加载营销活动统计
const loadCampaignStats = async () => {
  campaignStatsLoading.value = true
  try {
    const result = await dashboardService.getCampaignStats(5)
    if (result.success && result.data) {
      campaignStatsData.value = result.data
    }
  } catch (error) {
    console.error('加载营销活动统计失败:', error)
  } finally {
    campaignStatsLoading.value = false
  }
}

// 加载客户增长数据
const loadCustomerGrowth = async () => {
  customerGrowthLoading.value = true
  try {
    const result = await dashboardService.getCustomerGrowth(customerGrowthPeriod.value)
    if (result.success && result.data) {
      customerGrowthData.value = result.data
      customerGrowthRate.value = result.data.growth_rate
    }
  } catch (error) {
    console.error('加载客户增长数据失败:', error)
  } finally {
    customerGrowthLoading.value = false
  }
}

// 加载最近活动
const loadRecentActivities = async () => {
  recentActivitiesLoading.value = true
  try {
    const result = await dashboardService.getRecentActivities(5)
    if (result.success && result.data) {
      recentActivities.value = result.data
    }
  } catch (error) {
    console.error('加载最近活动失败:', error)
  } finally {
    recentActivitiesLoading.value = false
  }
}

// 跳转到营销活动页面
const goToCampaigns = () => {
  tabsStore.addTab({
    path: '/campaigns',
    name: 'Campaigns',
    params: {},
    query: {}
  })
}

// 查看营销活动详情
const viewCampaign = (campaignId) => {
  tabsStore.addTab({
    path: '/campaigns',
    name: 'Campaigns',
    params: {},
    query: { id: campaignId }
  })
}

// 下拉框控制方法
const toggleEmailTrendDropdown = () => {
  isEmailTrendOpen.value = !isEmailTrendOpen.value
  if (isEmailTrendOpen.value) {
    isCustomerGrowthOpen.value = false
  }
}

const selectEmailTrendDays = (days) => {
  emailTrendDays.value = days
  isEmailTrendOpen.value = false
  loadEmailTrend()
}

const toggleCustomerGrowthDropdown = () => {
  isCustomerGrowthOpen.value = !isCustomerGrowthOpen.value
  if (isCustomerGrowthOpen.value) {
    isEmailTrendOpen.value = false
  }
}

const selectCustomerGrowthPeriod = (period) => {
  customerGrowthPeriod.value = period
  isCustomerGrowthOpen.value = false
  loadCustomerGrowth()
}

// 点击外部关闭下拉框
const handleClickOutside = (event) => {
  if (emailTrendSelectRef.value && !emailTrendSelectRef.value.contains(event.target)) {
    isEmailTrendOpen.value = false
  }
  if (customerGrowthSelectRef.value && !customerGrowthSelectRef.value.contains(event.target)) {
    isCustomerGrowthOpen.value = false
  }
}

onMounted(() => {
  // 只有在用户已认证时才加载数据
  if (authStore.isAuthenticated) {
    loadDashboardData()
    loadEmailTrend()
    loadCampaignStats()
    loadCustomerGrowth()
    loadRecentActivities()
  }
  
  // 添加全局点击监听
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  // 移除全局点击监听
  document.removeEventListener('click', handleClickOutside)
})
</script>
